import {TableColumnsWithFilter} from "./_interfaces";
import React, {memo} from "react";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Tooltip, {tooltipClasses, TooltipProps} from "@mui/material/Tooltip";
import {styled} from "@mui/system";
import {SxProps} from "@mui/material";
import {Theme} from "@mui/material/styles";

interface DataTableRowProps {
    row: {[key: string]: any};
    columns: TableColumnsWithFilter[];
    dense: boolean;
    hasSelect: boolean;
    isMdUp: boolean;
    isItemSelected: boolean | undefined;
    isRowSelectable: boolean;
    labelId: string;
    onRowClick: (event: React.MouseEvent<unknown>, id: number) => void;
}

const getTextFromReactNode = (node: React.ReactNode): string => {
    if (node === null || node === undefined || typeof node === "boolean") {
        return "";
    }

    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }

    if (Array.isArray(node)) {
        return node.map(getTextFromReactNode).join("");
    }

    if (React.isValidElement(node)) {
        return getTextFromReactNode(node.props.children);
    }

    return "";
};

const TooltipStyled = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}}/>
))(({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        color: "#fff",
        fontSize: "0.875rem",
        "& a": {
            color: theme.palette.primary.contrastText,
        },
        "& a:hover": {
            color: theme.palette.primary.light,
        }
    },
}));

const getCellSx = (
    column: TableColumnsWithFilter,
    dense: boolean,
    isMdUp: boolean,
): SxProps<Theme> => ({
    padding: dense ? "4px" : column.disablePadding ? 0 : "8px 12px",
    display: column.hidden ? "none" : "table-cell",
    position: column.sticky && isMdUp ? "sticky" : "static",
    right: column.sticky && isMdUp ? 0 : "auto",
    zIndex: column.sticky && isMdUp ? 1 : "auto",
    backgroundColor: column.sticky && isMdUp ? "background.paper" : "inherit",
    ...(column.sticky && isMdUp && {
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: "1px",
            backgroundColor: "rgba(253, 253, 253, 1)",
            boxShadow: "-2px 0px 10px 0px #98C0FF",
        },
        "tr:hover &": {
            backgroundColor: "rgb(199 212 235)",
        },
        ".Mui-selected &": {
            backgroundColor: "rgb(183 202 240)",
        },
    }),
});

const DataTableRow = memo(({
                               row,
                               columns,
                               dense,
                               hasSelect,
                               isMdUp,
                               isItemSelected,
                               isRowSelectable,
                               labelId,
                               onRowClick,
                           }: DataTableRowProps) => {
    const handleRowClick = (event: React.MouseEvent<unknown>) => {
        if (!isRowSelectable || !hasSelect) {
            return;
        }
        onRowClick(event, row.id);
    };

    const renderCellContent = (column: TableColumnsWithFilter, cellValue: React.ReactNode) => {
        if (!column.noWrap) {
            return cellValue;
        }

        const tooltipText = getTextFromReactNode(cellValue);
        const tooltipTextLength = column.tooltipTextLength ?? 20;
        const shouldUseTooltip = Boolean(column.tooltip && column.noWrap && tooltipText.length > tooltipTextLength);

        if (!shouldUseTooltip) {
            return (
                <Box
                    width="100%"
                    sx={{
                        display: "flex",
                        ...(column.numeric ? {justifyContent: "flex-end"} : {}),
                    }}
                >
                    <Typography
                        component="div"
                        variant="body2"
                        noWrap
                        sx={{
                            width: column.width ?? "auto",
                            maxWidth: column.width ?? "auto",
                        }}
                    >
                        {cellValue}
                    </Typography>
                </Box>
            );
        }

        return (
            <TooltipStyled
                title={
                    <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start" p={1}>
                        {cellValue}
                    </Box>
                }
                TransitionComponent={Fade}
                TransitionProps={{timeout: 200}}
            >
                <Typography
                    display="flex"
                    component="div"
                    variant="body2"
                    noWrap
                    sx={{
                        width: column.width ?? "auto",
                        maxWidth: column.width ?? "auto",
                    }}
                >
                    {cellValue}
                </Typography>
            </TooltipStyled>
        );
    };

    return (
        <TableRow
            hover
            onClick={handleRowClick}
            role="checkbox"
            aria-checked={isItemSelected}
            tabIndex={-1}
            selected={isItemSelected}
            sx={{cursor: isRowSelectable ? "pointer" : "default"}}
        >
            {hasSelect && (
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        checked={isRowSelectable ? isItemSelected : false}
                        inputProps={{
                            "aria-labelledby": labelId,
                        }}
                        disabled={!isRowSelectable}
                    />
                </TableCell>
            )}

            {columns.map((column) => (
                <TableCell
                    key={column.id}
                    align={column.numeric ? "right" : "left"}
                    {...(column.id === "id" ? {id: labelId, scope: "row"} : {})}
                    sx={getCellSx(column, dense, isMdUp)}
                >
                    {renderCellContent(column, row[column.id])}
                </TableCell>
            ))}
        </TableRow>
    );
});

export default DataTableRow;